import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { compact, uniq } from 'lodash';
import { TreeItem, TreeView } from '@mui/lab';
import { Box, Checkbox, Chip, CircularProgress, Stack } from '@mui/material';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { Button, Typography } from 'melp-design/components';
import { SystemColors } from 'melp-design/style';
import { useCompanies } from 'store/companies';
import { useDepartments } from 'store/departments';
import { useTeams } from 'store/teams';
import {
  buildOrgStructurePath,
  isOrgStructureDescendant,
  parseOrgStructurePath,
} from 'utils/general';

/**
 * A single selected path in the organisational structure. Can stop at any
 * level, i.e. company only, company + department or a full path down to a
 * team.
 */
export type OrgStructureSelection = string;

interface NodeState {
  isChecked: (path: string) => boolean;
  hasSelectedDescendant: (path: string) => boolean;
  onToggle: (path: string) => void;
}

interface NodeLabelProps {
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  onToggle: () => void;
}

const NodeLabel: FC<NodeLabelProps> = ({
  label,
  checked,
  indeterminate = false,
  onToggle,
}) => (
  <Stack direction="row" alignItems="center" gap={0.5} py={0.25}>
    <Checkbox
      color="primary"
      size="small"
      checked={checked}
      indeterminate={!checked && indeterminate}
      onClick={(e) => e.stopPropagation()}
      onChange={onToggle}
    />
    <Typography>{label}</Typography>
  </Stack>
);

const StatusNode: FC<{ nodeId: string; loading?: boolean }> = ({
  nodeId,
  loading = false,
}) => {
  const { t } = useTranslation();

  return (
    <TreeItem
      nodeId={nodeId}
      label={
        <Box py={0.75} pl={1}>
          {loading ? (
            <CircularProgress size={16} />
          ) : (
            <Typography color="textSecondary">{t('common.none')}</Typography>
          )}
        </Box>
      }
    />
  );
};

interface DepartmentsLevelProps extends NodeState {
  companyId: string;
}

const DepartmentsLevel: FC<DepartmentsLevelProps> = ({
  companyId,
  isChecked,
  hasSelectedDescendant,
  onToggle,
}) => {
  const { data: departments, isLoading } = useDepartments({
    companies: [companyId],
    pageSize: 99999,
  });

  if (isLoading) {
    return <StatusNode nodeId={`${companyId}-loading`} loading />;
  }

  if (!departments?.length) {
    return <StatusNode nodeId={`${companyId}-empty`} />;
  }

  return (
    <>
      {departments.map((department) => {
        const path = buildOrgStructurePath({
          companyId,
          departmentId: department.id,
        });
        const { teams } = department;

        return (
          <TreeItem
            key={path}
            nodeId={path}
            label={
              <NodeLabel
                label={department.name}
                checked={isChecked(path)}
                indeterminate={hasSelectedDescendant(path)}
                onToggle={() => onToggle(path)}
              />
            }
          >
            {teams.length
              ? teams.map((team) => {
                  const teamPath = buildOrgStructurePath({
                    companyId,
                    departmentId: department.id,
                    teamId: team.id,
                  });

                  return (
                    <TreeItem
                      key={teamPath}
                      nodeId={teamPath}
                      label={
                        <NodeLabel
                          label={team.name}
                          checked={isChecked(teamPath)}
                          onToggle={() => onToggle(teamPath)}
                        />
                      }
                    />
                  );
                })
              : null}
          </TreeItem>
        );
      })}
    </>
  );
};

interface Props {
  value?: OrgStructureSelection[];
  onApplyFilter: (value: OrgStructureSelection[]) => void;
  onClearFilter: () => void;
  onCancel: () => void;
}

export const OrgStructureFilter: FC<Props> = ({
  value,
  onApplyFilter,
  onClearFilter,
  onCancel,
}) => {
  const { t } = useTranslation();

  const [selections, setSelections] = useState<OrgStructureSelection[]>(
    value ?? [],
  );
  const [expanded, setExpanded] = useState<string[]>([]);

  const { data: companies, isLoading } = useCompanies({
    status: ['active'],
    pageSize: 99999,
  });

  const selectedIds = selections.map(parseOrgStructurePath);
  const selectedDepartmentIds = uniq(
    compact(selectedIds.map((ids) => ids.departmentId)),
  );
  const selectedTeamIds = uniq(compact(selectedIds.map((ids) => ids.teamId)));

  // Department and team names are resolved separately so selected chips stay
  // labelled even when those nodes have not been expanded in the tree.
  const { data: selectedDepartments } = useDepartments(
    { departments: selectedDepartmentIds, pageSize: 99999 },
    { enabled: !!selectedDepartmentIds.length },
  );
  const { data: selectedTeams } = useTeams(
    { teams: selectedTeamIds, pageSize: 99999 },
    { enabled: !!selectedTeamIds.length },
  );

  const getSelectionLabel = (path: OrgStructureSelection) => {
    const { companyId, departmentId, teamId } = parseOrgStructurePath(path);

    return (
      compact([
        companies?.items.find((company) => company.id === companyId)?.name,
        selectedDepartments?.find(
          (department) => department.id === departmentId,
        )?.name,
        selectedTeams?.find((team) => team.id === teamId)?.name,
      ]).join(' → ') || '…'
    );
  };

  const isChecked = (path: string) => selections.includes(path);
  const hasSelectedDescendant = (path: string) =>
    selections.some((selection) => isOrgStructureDescendant(selection, path));

  const toggleSelection = (path: string) => {
    setSelections((current) => {
      if (current.includes(path)) {
        return current.filter((selection) => selection !== path);
      }
      // Broader and narrower paths are redundant once this one is selected,
      // e.g. selecting a whole company drops its selected departments/teams
      // and vice versa.
      return [
        ...current.filter(
          (selection) =>
            !isOrgStructureDescendant(path, selection) &&
            !isOrgStructureDescendant(selection, path),
        ),
        path,
      ];
    });
  };

  const renderTree = () => {
    if (isLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          p={2}
          minHeight={150}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (!companies?.items.length) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          p={2}
          minHeight={150}
        >
          <Typography>{t('common.noFilterOptions')}</Typography>
        </Box>
      );
    }

    return (
      <TreeView
        expanded={expanded}
        onNodeToggle={(_: unknown, nodeIds: string[]) => setExpanded(nodeIds)}
        disableSelection
        defaultCollapseIcon={<ExpandMore />}
        defaultExpandIcon={<ChevronRight />}
      >
        {companies.items.map((company) => {
          const path = buildOrgStructurePath({ companyId: company.id });

          return (
            <TreeItem
              key={path}
              nodeId={path}
              label={
                <NodeLabel
                  label={company.name}
                  checked={isChecked(path)}
                  indeterminate={hasSelectedDescendant(path)}
                  onToggle={() => toggleSelection(path)}
                />
              }
            >
              {expanded.includes(path) ? (
                <DepartmentsLevel
                  companyId={company.id}
                  isChecked={isChecked}
                  hasSelectedDescendant={hasSelectedDescendant}
                  onToggle={toggleSelection}
                />
              ) : (
                <StatusNode nodeId={`${path}-placeholder`} loading />
              )}
            </TreeItem>
          );
        })}
      </TreeView>
    );
  };

  return (
    <>
      {selections.length ? (
        <Stack
          direction="row"
          flexWrap="wrap"
          gap={0.5}
          sx={{ px: 1, pt: 1, pb: 1 }}
        >
          {selections.map((selection) => (
            <Chip
              key={selection}
              label={getSelectionLabel(selection)}
              size="small"
              onDelete={() => toggleSelection(selection)}
            />
          ))}
        </Stack>
      ) : null}

      <Stack
        sx={{
          maxHeight: 320,
          minWidth: 380,
          overflow: 'auto',
          pt: selections.length ? 0 : 0.5,
        }}
      >
        {renderTree()}
      </Stack>

      <Stack
        sx={{
          p: 1,
          background: SystemColors.grey[98],
          borderBottomLeftRadius: 6,
          borderBottomRightRadius: 6,
        }}
        gap={1}
      >
        <Stack flexDirection="row" justifyContent="flex-end" gap={1}>
          {value?.length ? (
            <Button
              label={t('table.clear')}
              variant="neutral-outline"
              onClick={onClearFilter}
            />
          ) : null}
          <Button
            label={t('common.cancel')}
            variant="neutral-outline"
            onClick={onCancel}
          />
          <Button
            label={t('common.applyFilter')}
            variant="primary"
            onClick={() => onApplyFilter(selections)}
            disabled={!selections.length}
          />
        </Stack>
      </Stack>
    </>
  );
};
